import {storeToRefs} from 'pinia';
import {computed, unref} from 'vue';
import type {Ref} from 'vue';
import useApi from '~/composables/useApi';
import {useCompanyContextStore} from '~/store/company/companyContext';
import type {ClaimedUpload, UnclaimedUpload, UploadConfig} from '~/lib/types/uploads';
import {downloadAttachment as downloadRieAttachment} from '~/composables/uploads/downloadAttachment';

// in seperate .ts file, since adding it to a vue sfc borks typescript
export const resolveRieAttachment = async (attachmentId: number) => {
	const {GET} = useApi();
	const companyContextStore = useCompanyContextStore();
	const {currentRieId} = storeToRefs(companyContextStore);

	const rieId = unref(currentRieId);

	if (!rieId) {
		throw new Error('No rieId found');
	}

	return await GET('/rie/{rieId}/attachment/{attachmentId}', {
		params: {
			path: {
				rieId: rieId.toString(),
				attachmentId: attachmentId.toString()
			}
		},
		parseAs: 'blob'
	});
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useUploadInput = <T extends Record<string, any>>(
	formFields: Ref<T>,
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-explicit-any
	getAttachments: (formFields: T) => ({ id: any, upload: ClaimedUpload })[] = (formFields) => formFields.attachments ?? [],
	// eslint-disable-next-line no-unused-vars
	getNewAttachments: (formFields: T) => UnclaimedUpload[] = (formFields) => formFields.newAttachments ?? [],
	// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-explicit-any
	downloadAttachment: (attachment: ReturnType<typeof getAttachments>, id: any) => Promise<void> = downloadRieAttachment as any
) => {
	const uploadInputAttrs = (): UploadConfig => {
		return {
			canDownload: () => false,
			uploadFinished: (upload) => {
				getNewAttachments(formFields.value).push(upload);
			},
			uploadRemoved: (id) => {
				const attachments = getAttachments(formFields.value);
				const newAttachments = getNewAttachments(formFields.value);

				if (attachments) {
					const index = attachments.findIndex((attachment) => {
						return attachment.upload.uploadId === id;
					});
					if (index !== -1) {
						attachments.splice(index, 1);
					}
				}

				if (newAttachments) {
					const newAttachmentIndex = newAttachments.findIndex((attachment) => {
						return attachment.id === id;
					});
					if (newAttachmentIndex !== -1) {
						newAttachments.splice(newAttachmentIndex, 1);
					}
				}
			},
			download: (id: string) => {
				if (!getAttachments(formFields.value)) {
					throw new Error('No attachments found');
				}
				downloadAttachment(getAttachments(formFields.value), id)
					.then(() => {
						// Added `then()` just to please IntelliJ
					});
			},
			existingAttachments: computed(() => {
				return getAttachments(formFields.value).map(attachment => attachment.upload);
			})
		};

	};

	return {
		uploadInputAttrs
	};
};
